import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { type DialogProps, FormControl, Stack, useTheme } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { SubClassContainer } from './SubClassContainer';
import { CLASS_CREATE } from '../../../config/apiPaths';
import { useAdminSettings } from '../../../context/api';
import { ClassContext } from '../../../context/ClassContext';
import { type ClassRecordData } from '../../../context/types';
import {
    generateMeetingPasswordSettingsForSchooolRecordWithSettings,
    PasswordGenerator
} from '../../../utils/password-generator';
import { getUrls } from '../../../utils/url';
import { type ClassCreatePayload } from '../../../views/ClassModal/types';
import { groupItems } from '../../addSessions/AddSessionsFields';
import { ConfirmationDialog } from '../../common/dialog/ConfirmationDialog';
import ControlledInput from '../../forms/ControlledInput';
import ControlledSelect from '../../forms/ControlledSelect';

export type CreateSubClassDialogProps = DialogProps & {};

export const SubClassesCreateDialog = (props: CreateSubClassDialogProps) => {
    const { onClose, open } = props;
    const { t } = useTranslation('create_edit_class');
    const { api } = getUrls();
    const [confirming, setConfirming] = useState(false);
    const [isProgrammaticName, setIsProgrammaticName] = useState(false);
    const theme = useTheme();

    const { handleSubmit, control, getValues, setValue, reset, watch } = useForm({
        defaultValues: {
            name: '',
            description: '',
            classGroups: []
        }
    });
    const formRef = useRef<HTMLFormElement | null>(null);
    const [nameWatch, classGroupsWatch] = watch(['name', 'classGroups']);
    const { enqueueSnackbar } = useSnackbar();

    const { userUuid } = useContext(ClassContext);
    const { fetchSubClasses, mainClass } = useContainer(SubClassContainer);
    const { data: adminSettings } = useAdminSettings();

    const generatePassword = useCallback(() => {
        if (!adminSettings) {
            return new PasswordGenerator().generatePassword();
        }
        return new PasswordGenerator().generatePassword(
            generateMeetingPasswordSettingsForSchooolRecordWithSettings(adminSettings)
        );
    }, [adminSettings]);

    const handleClose = useCallback(() => {
        if (!confirming) {
            reset();
            onClose?.({}, 'backdropClick');
        }
    }, [confirming, onClose, reset]);

    const buildCreatePayload = useCallback((): ClassCreatePayload => {
        const { name, description, classGroups } = getValues();

        const classDataNew: ClassRecordData = {
            class_name: name,
            class_description: description,
            content_type: 'default',
            timezone: mainClass?.data?.timezone ?? '',
            class_code: ''
        };

        return {
            class_data: classDataNew,
            parent_id: mainClass?.sid,
            user_uuid: userUuid,
            status: 'published',
            meeting_obj: {
                topic: name,
                password: generatePassword(),
                agenda: '',
                settings: {
                    waiting_room: false,
                    meeting_authentication: false
                }
            },
            class_options: {},
            class_group_ids: classGroups || []
        };
    }, [generatePassword, getValues, mainClass?.data?.timezone, mainClass?.sid, userUuid]);

    const onSubmit = async () => {
        try {
            setConfirming(true);
            await axios.post(`${api}${CLASS_CREATE}`, buildCreatePayload());
            await fetchSubClasses(true);
        } catch (err: any) {
            switch (err?.response?.status) {
                case 429:
                    enqueueSnackbar(t('error.create-too-many'), { variant: 'error' });
                    break;
                default:
                    enqueueSnackbar(t('error.create'), { variant: 'error' });
            }
        }
        setConfirming(false);
        handleClose();
    };

    const submitForm = () => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    };

    const handleConfirmed = () => {
        submitForm();
    };

    useEffect(() => {
        if (classGroupsWatch?.length === 1) {
            if (!nameWatch) {
                const selectedGroup = classGroupsWatch[0];
                if (selectedGroup) {
                    setValue(
                        'name',
                        mainClass?.classGroups?.find((group) => selectedGroup === group.c_student_group_id)?.label || ''
                    );
                    setIsProgrammaticName(true);
                }
            }
        } else {
            if (isProgrammaticName) {
                setValue('name', '');
                setIsProgrammaticName(false);
            }
        }
    }, [nameWatch, classGroupsWatch, setValue, mainClass?.classGroups, isProgrammaticName]);

    return (
        <ConfirmationDialog
            id="edit-session"
            open={open}
            onClose={handleClose}
            title={t('create_edit_class:create-title')}
            confirmLabel={t('class:sub-class.create-button')}
            onConfirm={handleConfirmed}
            confirming={confirming}
            setConfirming={setConfirming}
        >
            <Stack component="form" ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                {!!mainClass?.classGroups?.length && (
                    <FormControl fullWidth>
                        <ControlledSelect
                            name="classGroups"
                            multiple={true}
                            label={t('calendar:student-groups')}
                            control={control}
                        >
                            {groupItems(mainClass.classGroups, mainClass.classGroupSets, t)}
                        </ControlledSelect>
                    </FormControl>
                )}
                <FormControl fullWidth>
                    <ControlledInput
                        type="text"
                        name="name"
                        control={control}
                        label={t('details.class-name')}
                        placeholder={t('details.enter-class-name')}
                        onChange={(e) => {
                            setValue('name', e.target.value);
                            setIsProgrammaticName(false);
                        }}
                        inputProps={{
                            style: {
                                color: isProgrammaticName ? theme.palette.text.secondary : theme.palette.text.primary
                            }
                        }}
                    />
                </FormControl>
                <FormControl fullWidth>
                    <ControlledInput
                        type="text"
                        name="description"
                        control={control}
                        label={t('details.description')}
                        placeholder={t('details.enter-description')}
                    />
                </FormControl>
            </Stack>
        </ConfirmationDialog>
    );
};
